'use client';

import { JsonLd } from '@/seo/JsonLd';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import { products } from '@/data/products';

export function ProductsSEO() {
  const url = toAbsoluteUrl('/urunler');
  const title = 'Bondex Sünger Fiyatları | 2026 En Güncel Fiyatlar - Echopan';
  const description =
    "Bondex sünger fiyatları, Tüm kalınlıklar, 100–200 DNS yoğunluk, bariyerli ve düz seçeneklere göre Türkiye'nin Üreticisi Echopan’da detaylı inceleyin.";
  const primaryProductImage = toAbsoluteUrl(products[0]?.imagePath ?? '/images/bondex.webp');
  const validFromStr = new Date().toISOString().split('T')[0];

  // Calculate priceValidUntil (1 year from now)
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  // ItemList schema for all products
  const itemListElements = products.map((product, index) => {
    const productSchema: any = {
      '@type': 'Product',
      '@id': `${toAbsoluteUrl(`/${product.slug}`)}#product`,
      name: product.name,
      image: [toAbsoluteUrl(product.imagePath)],
      description: product.shortDescription,
      brand: { '@type': 'Brand', name: 'Bondex' },
      url: toAbsoluteUrl(`/${product.slug}`),
      sku: product.sku,
      mpn: product.mpn,
      category: product.category,
    };

    // Add Offer if price exists
    if (product.priceTRY) {
      const offer: any = {
        '@type': 'Offer',
        url: toAbsoluteUrl(`/${product.slug}`),
        priceCurrency: 'TRY',
        price: String(product.priceTRY),
        validFrom: validFromStr,
        priceValidUntil: priceValidUntilStr,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
        hasMerchantReturnPolicy: { '@id': `${toAbsoluteUrl('/')}#merchantReturnPolicy` },
        shippingDetails: { '@id': `${toAbsoluteUrl('/')}#shippingDetails` },
      };

      // Add unitText if priceUnit is specified
      if (product.priceUnit === 'm2') {
        offer.unitText = 'm²';
        offer.priceSpecification = {
          '@type': 'UnitPriceSpecification',
          price: String(product.priceTRY),
          priceCurrency: 'TRY',
          unitCode: 'MTK', // Square meter
          unitText: 'm²',
        };
      }

      productSchema.offers = offer;
    }

    // Add AggregateRating if reviews exist
    if (product.reviews && product.reviews.length > 0) {
      const reviewCount = product.reviews.length;
      const ratingValue =
        product.reviews.reduce((sum, r) => sum + (Number.isFinite(r.ratingValue) ? r.ratingValue : 0), 0) / reviewCount;

      productSchema.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: ratingValue.toFixed(1),
        reviewCount,
        bestRating: '5',
        worstRating: '1',
      };
    }

    return {
      '@type': 'ListItem',
      position: index + 1,
      item: productSchema,
    };
  });

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${toAbsoluteUrl('/')}#organization`,
        name: 'bariyerlibondexsunger.com',
        url: toAbsoluteUrl('/'),
        logo: {
          '@type': 'ImageObject',
          url: toAbsoluteUrl('/images/bondex-logo.webp'),
          width: 512,
          height: 512,
        },
        contactPoint: [
          {
            '@type': 'ContactPoint',
            telephone: '+90 216 519 34 11',
            contactType: 'customer service',
            areaServed: 'TR',
            availableLanguage: ['tr'],
          },
          {
            '@type': 'ContactPoint',
            telephone: '+90 546 919 11 34',
            contactType: 'sales',
            areaServed: 'TR',
            availableLanguage: ['tr'],
          },
        ],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61575206163572',
          'https://x.com/KabinPods',
          'https://www.instagram.com/kabinpods/',
          'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
          'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
        ],
      },
      {
        '@type': 'WebSite',
        '@id': `${toAbsoluteUrl('/')}#website`,
        url: toAbsoluteUrl('/'),
        name: 'Bondex Sünger',
        description: 'Bondex sünger ve Bariyerli Bondex çözümleri ile ses yalıtımı projeleriniz için yüksek yoğunluklu süngerpan seçenekleri.',
        publisher: { '@id': `${toAbsoluteUrl('/')}#organization` },
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${url}?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
        inLanguage: 'tr-TR',
        datePublished: '2025-12-17',
        dateModified: new Date().toISOString().split('T')[0],
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: primaryProductImage,
          width: 1200,
          height: 750,
        },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        about: [
          { '@type': 'Thing', name: 'Bondex sünger' },
          { '@type': 'Thing', name: 'Bariyerli Bondex' },
          { '@type': 'Thing', name: 'ses yalıtım süngeri' },
          { '@type': 'Thing', name: 'akustik sünger' },
        ],
        mainEntity: { '@id': `${url}#itemlist` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Anasayfa',
            item: toAbsoluteUrl('/'),
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Ürünler',
            item: url,
          },
        ],
      },
      // MerchantReturnPolicy schema (shared across all products)
      {
        '@type': 'MerchantReturnPolicy',
        '@id': `${toAbsoluteUrl('/')}#merchantReturnPolicy`,
        applicableCountry: {
          '@type': 'Country',
          name: 'TR',
        },
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
      },
      // OfferShippingDetails schema (shared across all products)
      {
        '@type': 'OfferShippingDetails',
        '@id': `${toAbsoluteUrl('/')}#shippingDetails`,
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0',
          currency: 'TRY',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: {
            '@type': 'Country',
            name: 'TR',
          },
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 3,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 5,
            unitCode: 'DAY',
          },
          businessDays: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          },
          cutoffTime: '14:00',
        },
      },
      {
        '@type': 'ItemList',
        '@id': `${url}#itemlist`,
        numberOfItems: products.length,
        itemListElement: itemListElements,
      },
    ],
  };

  return (
    <JsonLd data={schema} />
  );
}

